import { ExportCsv, ExportPdf } from '@material-table/exporters'
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import { SaveAlt } from '@mui/icons-material'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'
import { useIntl } from 'react-intl'
import { isValidElement, ReactElement } from 'react'

interface ExportDataProps {
  slug: string
  data: any[]
  columns: any[]
  onExportStart?: () => void
  onExportEnd?: () => void
}

type ReactElementWithProps = ReactElement<{
  checked?: boolean
  value?: any
  [key: string]: any
}>

const ExportData = ({ slug, data, columns, onExportStart, onExportEnd }: ExportDataProps) => {
  const intl = useIntl()
  const translate = (id: string) => intl.formatMessage({ id })

  const extractBooleanFromElement = (element: ReactElementWithProps): boolean | null => {
    if (element.props?.checked !== undefined) {
      return !!element.props.checked
    }
    if (element.props?.value !== undefined && typeof element.props.value === 'boolean') {
      return element.props.value
    }
    return null
  }

  const convertValueForExport = (value: any): string => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }
    if (value === null || value === undefined) {
      return ''
    }
    if (isValidElement(value)) {
      const bool = extractBooleanFromElement(value as ReactElementWithProps)
      if (bool !== null) return bool ? 'Yes' : 'No'
      return ''
    }
    if (typeof value === 'object') {
      if ('checked' in value && typeof value.checked === 'boolean') {
        return value.checked ? 'Yes' : 'No'
      }
      if ('value' in value && typeof value.value === 'boolean') {
        return value.value ? 'Yes' : 'No'
      }
      return ''
    }
    return String(value)
  }

  const allDemographicsMap: Record<string, { name: string, type: string }> = {}
  data.forEach(row => {
    if (row.demographics && Array.isArray(row.demographics)) {
      row.demographics.forEach((demo: any) => {
        if (demo.name && demo.type) {
          allDemographicsMap[demo.type] = { name: demo.name, type: demo.type }
        }
      })
    }
  })

  const allDemographics = Object.values(allDemographicsMap).sort((a, b) => a.name.localeCompare(b.name))

  const columnsForExport = [
    ...columns
      .filter(col => col.field)
      .map(col => ({
        title: col.title || col.field,
        field: Array.isArray(col.field) ? col.field.join('_') : col.field
      })),
    ...allDemographics.map(demo => ({
      title: demo.name,
      field: `demographic_${demo.type}`
    }))
  ]

  const dataForExport = data.map(row => {
    const exportedRow: Record<string, any> = {}
    columns.forEach(col => {
      if (!col.field) return
      const fieldKeys = Array.isArray(col.field) ? col.field : [col.field]
      let rawValue: any = row
      for (const key of fieldKeys) {
        if (rawValue && typeof rawValue === 'object' && key in rawValue) {
          rawValue = rawValue[key]
        } else {
          rawValue = undefined
          break
        }
      }
      const value = col.render ? col.render(row, row) : rawValue
      let exportValue = convertValueForExport(value)
      if (exportValue === '' && value !== rawValue) {
        exportValue = convertValueForExport(rawValue)
      }
      const exportFieldName = Array.isArray(col.field) ? col.field.join('_') : col.field
      exportedRow[exportFieldName] = exportValue
    })

    const demoMap: Record<string, string> = {}
    if (row.demographics && Array.isArray(row.demographics)) {
      row.demographics.forEach((demo: any) => {
        if (demo.type) {
          demoMap[demo.type] = demo.value || ''
        }
      })
    }
    allDemographics.forEach(demo => {
      exportedRow[`demographic_${demo.type}`] = demoMap[demo.type] || ''
    })

    return exportedRow
  })

  const handleExport = async (exportType: 'csv' | 'pdf') => {
    const fileName = `${slug}-export-${new Date().toISOString().slice(0, 10)}`
    try {
      onExportStart && onExportStart()
      if (exportType === 'csv') {
        await ExportCsv(columnsForExport, dataForExport, fileName)
      } else {
        await ExportPdf(columnsForExport, dataForExport, fileName)
      }
    } finally {
      onExportEnd && onExportEnd()
    }
  }

  return (
    <Tooltip title={translate('table.toolbar.exportTitle')}>
      <div>
        <PopupState variant="popover" popupId="export-menu">
          {(popupState) => (
            <div>
              <IconButton
                {...bindTrigger(popupState)}
                size="small"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  popupState.open(e.currentTarget)
                }}
              >
                <SaveAlt />
              </IconButton>
              <Menu
                {...bindMenu(popupState)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                onClose={() => popupState.close()}
              >
                <MenuItem onClick={() => {
                  popupState.close()
                  handleExport('csv')
                }}>
                  Export as CSV
                </MenuItem>
                <MenuItem onClick={() => {
                  popupState.close()
                  handleExport('pdf')
                }}>
                  Export as PDF
                </MenuItem>
              </Menu>
            </div>
          )}
        </PopupState>
      </div>
    </Tooltip>
  )
}

export default ExportData
